/**
 * Sidebar panels for the add-in chat drawer: Workflows and Style.
 *
 * Workflows = bundled skills (fetched from the streaming service's GET
 * /skills: name + description + tag) plus the user's own custom skills
 * (users/{uid}/skills in Firestore, owner-writable). Tapping one fills the
 * composer with an explicit skill reference so the model routes it to
 * read_skill, then closes the drawer (no auto-send, so the user can add
 * specifics first). "Add skill" saves a custom skill; the backend merges
 * custom skills into <available_skills> and read_skill for this user.
 *
 * Style = the per-org firm style guide (read-only here; admins edit in the
 * console) plus the user's PERSONAL style guide
 * (users/{uid}/workspace/styleGuide), editable inline and injected on every
 * turn for this user only.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, CircularProgress, TextField, IconButton, MenuItem, InputAdornment } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { doc, getDoc, setDoc, deleteDoc, collection, getDocs } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../../shared/services/firebase';
import { useAuth } from '../../../../shared/contexts/UserContext';
import { useOrgAccess } from '../../../../shared/contexts';
import { getChatRadii, getChatBorders } from '../chatStyles';
import { buildFunctionUrl } from '../../../../shared/utils';
import { invalidateTemplateCache } from '../../../../shared/services/data/templateStore';
import { CloseIcon, DeleteOutlineIcon, EditIcon, SearchIcon } from '../../../../shared/icons';

/* global Office */

const STREAMING_BASE = process.env.REACT_APP_STREAMING_SERVICE_URL
  || 'https://claude-streaming-service-103376361923.us-central1.run.app';

const CONSOLE_FIRM_STYLE_URL = 'https://re-stack.com/settings/firmstyle';

const MAX_PERSONAL_GUIDE_CHARS = 8000;
const MAX_SKILL_INSTRUCTIONS_CHARS = 12000;

// "build-waterfall" -> "Build Waterfall"
const humanizeSkill = (name) =>
  String(name || '').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Load the user's full workflow list: bundled (GET /skills) plus their own
 * custom workflows (users/{uid}/skills). Shared by the sidebar panel and the
 * composer's slash-command menu.
 */
export async function fetchWorkflowIndex(currentUser, app = 'powerpoint') {
  const host = ['powerpoint', 'excel', 'word'].includes(app) ? app : 'powerpoint';
  const [bundled, custom] = await Promise.all([
    fetch(`${STREAMING_BASE}/skills?app=${encodeURIComponent(host)}`)
      .then((res) => res.json())
      .then((data) => (Array.isArray(data?.skills) ? data.skills : []))
      .catch(() => []),
    (async () => {
      if (!currentUser?.uid || !db) return [];
      try {
        const snap = await getDocs(collection(db, 'users', currentUser.uid, 'skills'));
        return snap.docs
          .map((d) => ({ id: d.id, source: 'custom', ...(d.data() || {}) }))
          .filter((s) => s.name && (!Array.isArray(s.apps) || s.apps.length === 0 || s.apps.includes(host)));
      } catch (e) {
        console.warn('Failed to load custom workflows:', e?.message);
        return [];
      }
    })(),
  ]);
  return { bundled, custom };
}

// "Quarterly Board Pack" -> "quarterly-board-pack" (the model references
// skills by this name in read_skill, so keep it url/slug shaped).
const slugifySkillName = (label) =>
  String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

// Office-aware external open (mirrors UIComponents.openExternalUrl): in the task
// pane window.open is unreliable, so prefer the Office dialog API when present.
const openExternalUrl = (url) => {
  try {
    if (typeof Office !== 'undefined' && Office.context?.ui?.openBrowserWindow) {
      Office.context.ui.openBrowserWindow(url);
      return;
    }
  } catch (e) { /* fall through */ }
  try {
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened && typeof window !== 'undefined' && window.location) window.location.assign(url);
  } catch (e) { /* ignore */ }
};

const MAX_TEMPLATES = 10;
const MAX_TEMPLATE_BYTES = 30 * 1024 * 1024; // 30 MB
const TEMPLATE_CONFIG = {
  powerpoint: {
    label: 'PowerPoint', extensions: ['.pptx', '.potx'], accept: '.pptx,.potx',
    contentType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  },
  excel: {
    label: 'Excel', extensions: ['.xlsx', '.xltx'], accept: '.xlsx,.xltx',
    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  word: {
    label: 'Word', extensions: ['.docx', '.dotx'], accept: '.docx,.dotx',
    contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
};

const templateMatchesApp = (item, app) => {
  const name = String(item?.fileName || item?.name || '').toLowerCase();
  return (TEMPLATE_CONFIG[app]?.extensions || TEMPLATE_CONFIG.powerpoint.extensions)
    .some((ext) => name.endsWith(ext));
};

/**
 * TemplatesSection - the user's app-specific template library in the
 * Workflows tab. The user can upload a compatible file and select an override
 * for the current host. The firm default is set in the admin console and is
 * read-only here.
 */
const TemplatesSection = ({ app = 'powerpoint' }) => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { orgId } = useOrgAccess();
  const [items, setItems] = useState([]);
  const [activeTemplateIds, setActiveTemplateIds] = useState({});
  // The firm's default template (admin-set in the console, read-only here).
  // Auto-populates the dropdown without overriding the open file.
  const [firmTemplate, setFirmTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const templatesDocRef = useCallback(() => (
    currentUser?.uid && db ? doc(db, 'users', currentUser.uid, 'workspace', 'templates') : null
  ), [currentUser?.uid]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dref = templatesDocRef();
        if (!dref) return;
        const snap = await getDoc(dref);
        const data = snap.exists() ? (snap.data() || {}) : {};
        if (!cancelled) {
          setItems(Array.isArray(data.items) ? data.items : []);
          setActiveTemplateIds({
            ...(data.activeTemplateId ? { powerpoint: data.activeTemplateId } : {}),
            ...(data.activeTemplateIds || {}),
          });
          if (data.firmTemplate?.downloadUrl) setFirmTemplate(data.firmTemplate);
        }
      } catch (e) { /* empty library */ } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [templatesDocRef]);

  // Pull the firm default template from the org (admin-managed) and
  // denormalize it into the workspace doc so the send path (templateStore)
  // can resolve it without org access.
  useEffect(() => {
    if (!orgId || !currentUser) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const idToken = await currentUser.getIdToken();
        const res = await fetch(buildFunctionUrl('getFirmStyleGuide', { forceProduction: true }), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        const remoteFirm = data?.firmTemplate?.downloadUrl ? data.firmTemplate : null;
        if (cancelled) return;
        setFirmTemplate(remoteFirm);
        const dref = templatesDocRef();
        if (dref) {
          await setDoc(dref, { firmTemplate: remoteFirm || null }, { merge: true });
          invalidateTemplateCache();
        }
      } catch (e) { /* firm template stays as cached */ }
    })();
    return () => { cancelled = true; };
  }, [orgId, currentUser, templatesDocRef]);

  const persist = useCallback(async (nextItems, nextActiveIds) => {
    const dref = templatesDocRef();
    if (!dref) return;
    await setDoc(dref, {
      items: nextItems,
      activeTemplateIds: nextActiveIds,
      updatedAt: new Date().toISOString(),
    }, { merge: true });
    invalidateTemplateCache();
  }, [templatesDocRef]);

  const handleUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !currentUser?.uid || !storage) return;
    setError('');
    const lower = (file.name || '').toLowerCase();
    const config = TEMPLATE_CONFIG[app] || TEMPLATE_CONFIG.powerpoint;
    if (!config.extensions.some((ext) => lower.endsWith(ext))) {
      setError(`${config.label} templates must be ${config.extensions.join(' or ')} files.`);
      return;
    }
    if (file.size > MAX_TEMPLATE_BYTES) {
      setError('Template too large (max 30 MB).');
      return;
    }
    if (items.length >= MAX_TEMPLATES) {
      setError(`Template limit reached (${MAX_TEMPLATES}). Delete one first.`);
      return;
    }
    setUploading(true);
    try {
      const id = `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const sanitized = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `users/${currentUser.uid}/templates/${id}-${sanitized}`;
      const snapshot = await uploadBytes(storageRef(storage, storagePath), file, {
        contentType: file.type || config.contentType,
        customMetadata: { originalName: file.name, uploadedAt: new Date().toISOString() },
      });
      const downloadUrl = await getDownloadURL(snapshot.ref);
      const item = {
        id,
        name: file.name.replace(/\.(pptx|potx|xlsx|xltx|docx|dotx)$/i, ''),
        fileName: file.name,
        storagePath,
        downloadUrl,
        bytes: file.size,
        uploadedAt: new Date().toISOString(),
      };
      const nextItems = [...items, item];
      // Uploading adds an option but does not override the open file.
      await persist(nextItems, activeTemplateIds);
      setItems(nextItems);
    } catch (e) {
      setError(e?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }, [items, activeTemplateIds, app, currentUser?.uid, persist]);

  const handleSelect = useCallback(async (nextId) => {
    const nextActiveIds = { ...activeTemplateIds, [app]: nextId };
    setActiveTemplateIds(nextActiveIds);
    try {
      await persist(items, nextActiveIds);
    } catch (e) {
      setError(e?.message || 'Failed to save selection.');
    }
  }, [items, activeTemplateIds, app, persist]);

  const handleDelete = useCallback(async (item) => {
    const nextItems = items.filter((t) => t.id !== item.id);
    const compatibleRemaining = nextItems.filter((template) => templateMatchesApp(template, app));
    const nextActiveIds = activeTemplateIds[app] === item.id
      ? { ...activeTemplateIds, [app]: compatibleRemaining[0]?.id || 'none' }
      : activeTemplateIds;
    try {
      await persist(nextItems, nextActiveIds);
      setItems(nextItems);
      setActiveTemplateIds(nextActiveIds);
      // Best-effort object cleanup; the metadata is already gone.
      if (item.storagePath && storage) {
        deleteObject(storageRef(storage, item.storagePath)).catch(() => { /* orphan is harmless */ });
      }
    } catch (e) {
      setError(e?.message || 'Failed to delete template.');
    }
  }, [items, activeTemplateIds, app, persist]);

  if (!currentUser?.uid) return null;

  const compatibleItems = items.filter((item) => templateMatchesApp(item, app));
  const compatibleFirmTemplate = templateMatchesApp(firmTemplate, app) ? firmTemplate : null;
  const activeTemplateId = activeTemplateIds[app] || 'none';
  const selectedTemplateLabel = activeTemplateId === 'none'
    ? 'Use the open file'
    : activeTemplateId === 'firm-default'
      ? `Firm template: ${compatibleFirmTemplate?.name || 'Default'}`
      : compatibleItems.find((item) => item.id === activeTemplateId)?.name || 'Use the open file';

  return (
    <Box sx={{ px: 1.25, pb: 2.5, mb: 2.25, borderBottom: `${getChatBorders(theme).width}px solid ${theme.palette.divider}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.65 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: theme.typography.pxToRem(12.5) }}>
          File template
        </Typography>
        <Button
          size="small"
          variant="text"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          sx={{ textTransform: 'none', fontSize: theme.typography.pxToRem(11.5), fontWeight: 600, minWidth: 0, px: 0.75 }}
        >
          {uploading ? 'Uploading...' : '+ Upload'}
        </Button>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5, fontSize: theme.typography.pxToRem(11), lineHeight: 1.5 }}>
        The open file is used by default. Choose a firm or personal template only when you want to override it.
      </Typography>

      {loading ? (
        <Box sx={{ height: 40, display: 'flex', alignItems: 'center' }}><CircularProgress size={14} /></Box>
      ) : (
        <TextField
          select
          size="small"
          fullWidth
          value={activeTemplateId}
          onChange={(e) => handleSelect(e.target.value)}
          SelectProps={{
            renderValue: () => selectedTemplateLabel,
            MenuProps: {
              PaperProps: {
                sx: {
                  mt: 0.5,
                  maxHeight: 300,
                  borderRadius: getChatRadii(theme).card,
                  boxShadow: theme.shadows[6],
                  '& .MuiMenuItem-root': {
                    minHeight: 42,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: theme.typography.pxToRem(12.5),
                  },
                },
              },
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: 40,
              borderRadius: getChatRadii(theme).nested,
              fontSize: theme.typography.pxToRem(12.5),
              fontFamily: theme.typography.fontFamily,
              backgroundColor: alpha(theme.palette.background.paper, 0.7),
            },
          }}
        >
          <MenuItem value="none">
            <Box>
              <Typography sx={{ fontSize: 'inherit', fontWeight: 500 }}>Use the open file</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: theme.typography.pxToRem(10.5) }}>
                Recommended
              </Typography>
            </Box>
          </MenuItem>
          {compatibleFirmTemplate && (
            <MenuItem value="firm-default">
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 500 }}>
                  {compatibleFirmTemplate.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: theme.typography.pxToRem(10.5) }}>
                  Firm template
                </Typography>
              </Box>
            </MenuItem>
          )}
          {compatibleItems.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0, gap: 1 }}>
                <Typography component="span" noWrap sx={{ fontSize: 'inherit', minWidth: 0 }}>
                  {item.name}
                </Typography>
                <IconButton
                  size="small"
                  title="Delete this template"
                  onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                  sx={{ width: 26, height: 26, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>
            </MenuItem>
          ))}
        </TextField>
      )}

      {!loading && compatibleItems.length === 0 && !compatibleFirmTemplate && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.disabled', fontSize: theme.typography.pxToRem(10.75) }}>
          No override templates uploaded yet.
        </Typography>
      )}

      {error && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'error.main', fontSize: theme.typography.pxToRem(11) }}>
          {error}
        </Typography>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={(TEMPLATE_CONFIG[app] || TEMPLATE_CONFIG.powerpoint).accept}
        style={{ display: 'none' }}
        onChange={handleUpload}
      />
    </Box>
  );
};

export const WorkflowsPanel = ({ app = 'powerpoint', onSuggestionClick, onClose, onEditorOpenChange = null }) => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [bundled, setBundled] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Tell the drawer when the editor is open so it can expand: the multiline
  // instructions field is unreadable at list width. Reset on unmount (tab
  // switch / drawer close) so the drawer never sticks wide.
  useEffect(() => {
    onEditorOpenChange?.(adding);
    return () => onEditorOpenChange?.(false);
  }, [adding, onEditorOpenChange]);
  // Non-null while the form edits an EXISTING custom workflow (its doc id).
  // Bundled workflows are read-only source material: "Customize" prefills
  // the form with their full content and saves as a NEW custom workflow.
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', tag: '', description: '', instructions: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [loadingSkillName, setLoadingSkillName] = useState(null);

  const reload = useCallback(async () => {
    const { bundled: bundledList, custom: customList } = await fetchWorkflowIndex(currentUser, app);
    setBundled(bundledList);
    setCustom(customList);
  }, [currentUser, app]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { bundled: bundledList, custom: customList } = await fetchWorkflowIndex(currentUser, app);
        if (!cancelled) {
          setBundled(bundledList);
          setCustom(customList);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, app]);

  const closeForm = () => {
    setForm({ name: '', tag: '', description: '', instructions: '' });
    setAdding(false);
    setEditingId(null);
    setFormError('');
  };

  const handleSaveSkill = async () => {
    const slug = slugifySkillName(form.name);
    if (!slug) { setFormError('Give the workflow a name.'); return; }
    if (!form.instructions.trim()) { setFormError('Add the instructions the agent should follow.'); return; }
    // A rename may not collide with any OTHER workflow; re-saving under the
    // same name while editing is fine.
    const taken = [...bundled, ...custom].some((s) => s.name === slug && s.id !== editingId);
    if (taken) { setFormError(`A workflow named "${slug}" already exists.`); return; }
    if (!currentUser?.uid || !db) { setFormError('Sign in to save workflows.'); return; }

    setSaving(true);
    setFormError('');
    try {
      const existing = editingId ? custom.find((s) => s.id === editingId) : null;
      await setDoc(doc(db, 'users', currentUser.uid, 'skills', slug), {
        name: slug,
        title: form.name.trim().slice(0, 80),
        tag: form.tag.trim().slice(0, 40),
        apps: [app],
        description: form.description.trim().slice(0, 300),
        instructions: form.instructions.trim().slice(0, MAX_SKILL_INSTRUCTIONS_CHARS),
        createdAt: existing?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      // Renamed while editing: the doc id IS the slug, so remove the old doc.
      if (editingId && editingId !== slug) {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'skills', editingId));
      }
      await reload();
      closeForm();
    } catch (e) {
      setFormError(e?.message || 'Failed to save the workflow.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSkill = async (skillId) => {
    if (!currentUser?.uid || !db) return;
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'skills', skillId));
      setCustom((prev) => prev.filter((s) => s.id !== skillId));
      if (editingId === skillId) closeForm();
    } catch (e) {
      console.warn('Failed to delete workflow:', e?.message);
    }
  };

  // Open the form prefilled with an existing CUSTOM workflow for editing.
  const handleEditCustom = (skill) => {
    setForm({
      name: skill.title || humanizeSkill(skill.name),
      tag: skill.tag || '',
      description: skill.description || '',
      instructions: skill.instructions || '',
    });
    setEditingId(skill.id);
    setAdding(true);
    setFormError('');
  };

  // Bundled workflows are read-only templates. "Customize" pulls the full
  // SKILL.md body from the backend and opens the form as a NEW custom
  // workflow the user owns and can edit freely (Anthropic-style fork).
  const handleCustomizeBundled = async (skill) => {
    setLoadingSkillName(skill.name);
    setFormError('');
    try {
      const res = await fetch(`${STREAMING_BASE}/skills/${encodeURIComponent(skill.name)}`);
      const data = await res.json().catch(() => ({}));
      const full = data?.skill || {};
      setForm({
        name: `${skill.title || humanizeSkill(skill.name)} Custom`,
        tag: skill.tag || '',
        description: full.description || skill.description || '',
        instructions: full.instructions || '',
      });
      setEditingId(null);
      setAdding(true);
    } catch (e) {
      setFormError('Could not load this workflow. Try again.');
      setAdding(true);
    } finally {
      setLoadingSkillName(null);
    }
  };

  const allSkills = [...bundled, ...custom];
  const categories = [...new Set(allSkills.map((skill) => String(skill.tag || '').trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visibleSkills = allSkills.filter((skill) => {
    if (categoryFilter !== 'all' && skill.tag !== categoryFilter) return false;
    if (!normalizedQuery) return true;
    return [skill.title, skill.name, skill.description, skill.tag]
      .some((value) => String(value || '').toLowerCase().includes(normalizedQuery));
  });

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: getChatRadii(theme).nested,
      fontSize: theme.typography.pxToRem(12.5),
    },
    '& .MuiInputLabel-root': { fontSize: theme.typography.pxToRem(12.5) },
  };

  return (
    <Box sx={{ px: 0.75, py: 1, pb: 3 }}>
      {/* Template library first: templates are optional overrides for new work
          when the open file does not already provide the intended design. */}
      <TemplatesSection app={app} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25, mb: 1.25 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: theme.typography.pxToRem(12.5) }}>
          Workflow library
        </Typography>
        {!adding && (
          <Button
            size="small"
            variant="text"
            onClick={() => { setAdding(true); setFormError(''); }}
            sx={{ textTransform: 'none', fontSize: theme.typography.pxToRem(11.5), fontWeight: 600, minWidth: 0, px: 0.75 }}
          >
            + Add workflow
          </Button>
        )}
      </Box>

      {!adding && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0.75, px: 1.25, mb: 1.5 }}>
          <TextField
            size="small"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search workflows"
            inputProps={{ 'aria-label': 'Search workflows' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, borderRadius: getChatRadii(theme).nested, fontSize: theme.typography.pxToRem(11.75) },
            }}
          />
          <TextField
            select
            size="small"
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            inputProps={{ 'aria-label': 'Workflow category' }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, borderRadius: getChatRadii(theme).nested, fontSize: theme.typography.pxToRem(11.5) },
            }}
          >
            <MenuItem value="all">All categories</MenuItem>
            {categories.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}
          </TextField>
        </Box>
      )}

      {adding && (
        <Box
          sx={{
            mx: 1.25, mb: 2, p: 1.5,
            borderRadius: getChatRadii(theme).card,
            backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.045 : 0.025),
            display: 'flex', flexDirection: 'column', gap: 1.25,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: theme.typography.pxToRem(13) }}>
              {editingId ? 'Edit workflow' : 'New workflow'}
            </Typography>
            <IconButton size="small" title="Close editor" onClick={closeForm} sx={{ width: 28, height: 28, color: 'text.secondary' }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 160px' }, gap: 1 }}>
            <Box>
              <Typography component="label" sx={{ display: 'block', mb: 0.45, color: 'text.secondary', fontSize: theme.typography.pxToRem(11), fontWeight: 500 }}>
                Name
              </Typography>
              <TextField size="small" hiddenLabel value={form.name} fullWidth sx={fieldSx}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </Box>
            <Box>
              <Typography component="label" sx={{ display: 'block', mb: 0.45, color: 'text.secondary', fontSize: theme.typography.pxToRem(11), fontWeight: 500 }}>
                Category
              </Typography>
              <TextField select size="small" hiddenLabel value={form.tag} fullWidth sx={fieldSx}
                onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))}>
                <MenuItem value="">Uncategorized</MenuItem>
                {[...new Set([...categories, form.tag].filter(Boolean))].map((category) => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
          <Box>
            <Typography component="label" sx={{ display: 'block', mb: 0.45, color: 'text.secondary', fontSize: theme.typography.pxToRem(11), fontWeight: 500 }}>
              Purpose
            </Typography>
            <TextField size="small" hiddenLabel value={form.description} fullWidth sx={fieldSx}
              placeholder="What should this workflow accomplish?"
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </Box>
          <Box>
            <Typography component="label" sx={{ display: 'block', mb: 0.45, color: 'text.secondary', fontSize: theme.typography.pxToRem(11), fontWeight: 500 }}>
              Instructions for the agent
            </Typography>
          <TextField
            size="small" hiddenLabel value={form.instructions}
            multiline minRows={7} maxRows={12} fullWidth sx={{
              ...fieldSx,
              '& textarea': { resize: 'vertical', minHeight: 132, maxHeight: 320, overflow: 'auto' },
            }}
            placeholder="Add the steps and guardrails the agent should follow."
            onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))}
          />
          </Box>
          {formError && (
            <Typography variant="caption" sx={{ color: 'error.main', fontSize: theme.typography.pxToRem(11) }}>
              {formError}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end', pt: 0.25 }}>
            <Button size="small" variant="outlined" disabled={saving} sx={{ textTransform: 'none', borderRadius: getChatRadii(theme).pill }}
              onClick={closeForm}>
              Cancel
            </Button>
            <Button size="small" variant="contained" disableElevation disabled={saving} sx={{ textTransform: 'none', borderRadius: getChatRadii(theme).pill }}
              onClick={handleSaveSkill}>
              {saving ? 'Saving...' : (editingId ? 'Save changes' : 'Save workflow')}
            </Button>
          </Box>
        </Box>
      )}

      {loading && !adding && (
        <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={18} /></Box>
      )}

      {!loading && visibleSkills.length === 0 && !adding && (
        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2" sx={{ fontSize: theme.typography.pxToRem(12.5) }}>
            {allSkills.length === 0 ? 'No workflows available.' : 'No workflows match your search.'}
          </Typography>
        </Box>
      )}

      {visibleSkills.map((skill) => (
        <Box
          key={`${skill.source || 'bundled'}-${skill.name}`}
          sx={{
            display: 'flex', alignItems: 'center',
            mb: 0.45,
            borderRadius: getChatRadii(theme).nested,
            '&:hover': { backgroundColor: theme.palette.action.hover },
            '&:hover .skill-actions': { opacity: 1 },
          }}
        >
          <Box
            component="button"
            type="button"
            title={skill.description || undefined}
            onClick={() => {
              onSuggestionClick?.(`/${skill.name} `);
              onClose?.();
            }}
            sx={{
              display: 'flex', alignItems: 'center',
              flex: 1, minWidth: 0, textAlign: 'left', appearance: 'none',
              background: 'transparent', border: 'none', cursor: 'pointer',
              px: 1.25, py: 1.1, borderRadius: getChatRadii(theme).nested,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  minWidth: 0, fontWeight: 600,
                  fontSize: theme.typography.pxToRem(12.5), color: 'text.primary',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {skill.title || humanizeSkill(skill.name)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block', mt: 0.2,
                  fontSize: theme.typography.pxToRem(10.75), color: 'text.secondary',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {skill.description || (skill.source === 'custom' ? 'Custom workflow' : 'Built-in workflow')}
              </Typography>
            </Box>
          </Box>
          <Box className="skill-actions" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mr: 0.65, opacity: 0.58, transition: 'opacity 0.15s ease', flexShrink: 0 }}>
            <IconButton
              size="small"
              title={skill.source === 'custom' ? 'Edit this workflow' : 'Edit a copy of this workflow'}
              disabled={loadingSkillName === skill.name}
              onClick={() => (skill.source === 'custom' ? handleEditCustom(skill) : handleCustomizeBundled(skill))}
              sx={{ width: 28, height: 28, color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
            >
              {loadingSkillName === skill.name ? <CircularProgress size={12} /> : <EditIcon sx={{ fontSize: 15 }} />}
            </IconButton>
            {skill.source === 'custom' && (
              <IconButton
                size="small"
                title="Delete this workflow"
                onClick={() => handleDeleteSkill(skill.id)}
                sx={{ width: 28, height: 28, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 15 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const FirmStylePanel = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { orgId, isOrgAdmin } = useOrgAccess();
  const [guide, setGuide] = useState('');
  const [loading, setLoading] = useState(true);

  // Personal style guide (users/{uid}/workspace/styleGuide) — every signed-in
  // user has one, org or not.
  const [personalDraft, setPersonalDraft] = useState('');
  const [personalSaved, setPersonalSaved] = useState('');
  const [personalSaving, setPersonalSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Load the personal guide for everyone; the firm guide only with an org.
      try {
        if (currentUser?.uid && db) {
          const snap = await getDoc(doc(db, 'users', currentUser.uid, 'workspace', 'styleGuide'));
          const text = snap.exists() ? String(snap.data()?.guide || '') : '';
          if (!cancelled) { setPersonalDraft(text); setPersonalSaved(text); }
        }
      } catch (e) { /* leave empty */ }

      if (!orgId) { if (!cancelled) setLoading(false); return; }
      try {
        const idToken = await currentUser.getIdToken();
        const res = await fetch(buildFunctionUrl('getFirmStyleGuide', { forceProduction: true }), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled) setGuide(typeof data?.guide === 'string' ? data.guide : '');
      } catch (e) {
        /* leave empty */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, orgId]);

  const handleSavePersonal = async () => {
    if (!currentUser?.uid || !db) return;
    setPersonalSaving(true);
    try {
      const text = personalDraft.trim().slice(0, MAX_PERSONAL_GUIDE_CHARS);
      await setDoc(doc(db, 'users', currentUser.uid, 'workspace', 'styleGuide'), {
        guide: text,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      setPersonalSaved(text);
      setPersonalDraft(text);
    } catch (e) {
      console.warn('Failed to save personal style guide:', e?.message);
    } finally {
      setPersonalSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={20} /></Box>;
  }

  const personalDirty = personalDraft.trim() !== personalSaved.trim();

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Personal style guide — applied to this user's work only. */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: theme.typography.pxToRem(13), mb: 0.5 }}>
          Your style
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontSize: theme.typography.pxToRem(12) }}>
          Applied to everything you make, on top of any firm guide. Palette, fonts, phrasing, layout habits.
        </Typography>
        <TextField
          size="small"
          multiline
          minRows={4}
          maxRows={12}
          fullWidth
          placeholder={'e.g. Prefer navy #0B1F3A accents. Titles are complete sentences. Keep charts unlabelled unless asked.'}
          value={personalDraft}
          onChange={(e) => setPersonalDraft(e.target.value)}
          inputProps={{ maxLength: MAX_PERSONAL_GUIDE_CHARS }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: getChatRadii(theme).nested,
              fontSize: theme.typography.pxToRem(12.5),
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            size="small"
            variant="contained"
            disableElevation
            disabled={!personalDirty || personalSaving}
            onClick={handleSavePersonal}
            sx={{ textTransform: 'none' }}
          >
            {personalSaving ? 'Saving...' : personalDirty ? 'Save' : 'Saved'}
          </Button>
        </Box>
      </Box>

      {/* Firm style guide — org-wide, admin-managed. */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: theme.typography.pxToRem(13), mb: 0.5 }}>
          Firm style
        </Typography>
        {!orgId ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: theme.typography.pxToRem(12), lineHeight: 1.5 }}>
            A firm style guide is available on a team plan. It applies house rules (palette, fonts, title conventions) to every deck automatically.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontSize: theme.typography.pxToRem(12) }}>
              Applied to everyone at your firm. {isOrgAdmin ? 'Edit it in the console.' : 'Set by your firm admins.'}
            </Typography>
            {guide ? (
              <Box sx={{
                p: 1.5, borderRadius: getChatRadii(theme).card,
                border: getChatBorders(theme).resting,
                backgroundColor: alpha(theme.palette.text.primary, 0.02),
              }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: theme.typography.pxToRem(12.5), lineHeight: 1.5, color: 'text.primary' }}>
                  {guide}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: theme.typography.pxToRem(12.5) }}>
                No firm style guide set yet.{isOrgAdmin ? ' Add one in the console.' : ''}
              </Typography>
            )}
            {isOrgAdmin && (
              <Button size="small" variant="text" onClick={() => openExternalUrl(CONSOLE_FIRM_STYLE_URL)} sx={{ mt: 1, textTransform: 'none' }}>
                Edit in console
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
